import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Skeleton,
  alpha,
  useTheme,
  keyframes,
} from "@mui/material";
import {
  AutoAwesomeOutlined,
  CampaignOutlined,
  Face2Outlined,
  LibraryMusicOutlined,
  VideoLibraryOutlined,
} from "@mui/icons-material";

const drift = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(12px, -18px) scale(1.05); }
  66% { transform: translate(-8px, 10px) scale(0.96); }
`;

const pulseRing = keyframes`
  0% { transform: scale(0.92); opacity: 0.5; }
  50% { transform: scale(1.08); opacity: 0.9; }
  100% { transform: scale(0.92); opacity: 0.5; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FEATURE_ICONS = {
  ads: CampaignOutlined,
  characters: Face2Outlined,
  music: LibraryMusicOutlined,
  video: VideoLibraryOutlined,
};

export default function AuthVisualPanel({
  web,
  lang,
  siteName,
  logoUrl,
  isMobile,
}) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || theme.palette.info.main;

  const [mouse, setMouse] = React.useState({ x: 0.5, y: 0.5 });
  const [featureIdx, setFeatureIdx] = React.useState(0);

  const features = React.useMemo(
    () => [
      {
        key: "ads",
        title: lang?.authFeatureAds || "Product Ads",
        desc:
          lang?.authFeatureAdsDesc ||
          "UGC-style video ads with AI creators and your product in frame.",
      },
      {
        key: "characters",
        title: lang?.authFeatureCharacters || "AI Characters",
        desc:
          lang?.authFeatureCharactersDesc ||
          "Build on-brand influencers for every campaign and niche.",
      },
      {
        key: "music",
        title: lang?.authFeatureMusic || "Music Studio",
        desc:
          lang?.authFeatureMusicDesc ||
          "Generate lyrics and tracks that match your brand vibe.",
      },
      {
        key: "video",
        title: lang?.authFeatureVideo || "Video Agent",
        desc:
          lang?.authFeatureVideoDesc ||
          "Script-to-video workflows powered by leading AI models.",
      },
    ],
    [lang],
  );

  React.useEffect(() => {
    const id = setInterval(() => {
      setFeatureIdx((i) => (i + 1) % features.length);
    }, 4200);
    return () => clearInterval(id);
  }, [features.length]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMouse({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  };

  const active = features[featureIdx];
  const FeatureIcon = FEATURE_ICONS[active.key] || AutoAwesomeOutlined;

  const parallax = (strength) => ({
    transform: `translate(${(mouse.x - 0.5) * strength}px, ${(mouse.y - 0.5) * strength}px)`,
    transition: "transform 0.35s ease-out",
  });

  return (
    <Box
      onMouseMove={!isMobile ? handleMouseMove : undefined}
      sx={{
        flex: { xs: "0 0 auto", md: 1 },
        minHeight: { xs: 200, md: "100vh" },
        py: { xs: 4, md: 0 },
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(145deg, #0a0f1e 0%, ${alpha(primary, 0.35)} 45%, ${alpha(secondary, 0.25)} 100%)`
            : `linear-gradient(145deg, ${alpha(primary, 0.92)} 0%, ${alpha(secondary, 0.85)} 55%, ${primary} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Ambient orbs */}
      <Box
        sx={{
          position: "absolute",
          width: 420,
          height: 420,
          borderRadius: "50%",
          top: "-12%",
          left: "-8%",
          background: `radial-gradient(circle, ${alpha("#fff", 0.18)} 0%, transparent 65%)`,
          animation: `${drift} 14s ease-in-out infinite`,
          ...parallax(28),
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 320,
          height: 320,
          borderRadius: "50%",
          bottom: "-10%",
          right: "-6%",
          background: `radial-gradient(circle, ${alpha(secondary, 0.35)} 0%, transparent 70%)`,
          animation: `${drift} 11s ease-in-out infinite reverse`,
          ...parallax(-22),
          pointerEvents: "none",
        }}
      />

      {/* Grid overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.12,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
          maskImage: "radial-gradient(ellipse 80% 70% at 50% 50%, black 10%, transparent 75%)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          px: { xs: 3, md: 6 },
          py: { xs: 2, md: 4 },
          maxWidth: 520,
          width: "100%",
          animation: `${fadeUp} 0.7s ease-out`,
        }}
      >
        {/* Logo + name */}
        {web === null ? (
          <Stack spacing={2} alignItems="flex-start" mb={3}>
            <Skeleton variant="rounded" width={56} height={56} sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
            <Skeleton variant="text" width={180} height={40} sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
          </Stack>
        ) : (
          <Stack
            component={RouterLink}
            to="/"
            direction="row"
            spacing={2}
            alignItems="center"
            mb={2}
            aria-label={`${siteName} home`}
            sx={{
              textDecoration: "none",
              color: "inherit",
              cursor: "pointer",
              width: "fit-content",
              borderRadius: 2,
              transition: "opacity 0.15s ease, transform 0.15s ease",
              "&:hover": { opacity: 0.92, transform: "translateY(-1px)" },
              "&:focus-visible": {
                outline: "2px solid rgba(255,255,255,0.55)",
                outlineOffset: 4,
              },
            }}
          >
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt=""
                sx={{
                  width: { xs: 44, md: 56 },
                  height: { xs: 44, md: 56 },
                  objectFit: "contain",
                  borderRadius: 2,
                  filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.35))",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: 44, md: 56 },
                  height: { xs: 44, md: 56 },
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(12px)",
                  border: "2px solid rgba(255,255,255,0.35)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                }}
              >
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.4rem" }}>
                  {siteName.charAt(0).toUpperCase()}
                </Typography>
              </Box>
            )}
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                color: "#fff",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                textShadow: "0 4px 24px rgba(0,0,0,0.25)",
              }}
            >
              {siteName}
            </Typography>
          </Stack>
        )}

        <Typography
          variant="body1"
          sx={{
            color: "rgba(255,255,255,0.82)",
            fontWeight: 400,
            lineHeight: 1.6,
            mb: 3,
            maxWidth: 400,
          }}
        >
          {lang?.brandTagline || "Create. Innovate. Inspire."}
        </Typography>

        {!isMobile && (
          <>
            {/* Feature spotlight */}
            <Box
              key={active.key}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.22)",
                boxShadow: "0 16px 48px rgba(0,0,0,0.18)",
                animation: `${fadeUp} 0.5s ease-out`,
                mb: 2.5,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: `${pulseRing} 2.5s ease-in-out infinite`,
                  }}
                >
                  <FeatureIcon sx={{ color: "#fff", fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 700, mb: 0.5 }}>
                    {active.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.55 }}>
                    {active.desc}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={0.75} mt={2}>
                {features.map((f, i) => (
                  <Box
                    key={f.key}
                    sx={{
                      flex: 1,
                      height: 4,
                      borderRadius: 99,
                      bgcolor: i === featureIdx ? "#fff" : "rgba(255,255,255,0.25)",
                      transition: "background-color 0.35s ease, transform 0.35s ease",
                      transform: i === featureIdx ? "scaleY(1.2)" : "scaleY(1)",
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <Stack direction="row" flexWrap="wrap" gap={1}>
              {[
                lang?.authPillAi || "AI-powered",
                lang?.authPillFast || "Fast workflows",
                lang?.authPillSecure || "Secure login",
              ].map((label) => (
                <Chip
                  key={label}
                  label={label}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.2)",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }}
                />
              ))}
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}
